/* eslint-disable no-unsafe-optional-chaining */
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useEffectOnce } from "@lib-shared/hooks";
import { BusinessPage } from "core/constants";
import ApplicationAction from "core/constants/application-action";
import { AppContext } from "core/context";
import { useUpdatePage } from "core/hooks";
import { tokenCheckInterval } from "core/instances";
import { logApiError } from "core/lib";
import getUserDetailsAsync, { userStatus } from "core/lib/get-user-details";
import Style from "core/lib/style";
import UrlUtils from "core/lib/url-utils";
import AccountNotFound from "./components/account-not-found";
import { Notice, ModalAlert } from "components/common-ui";
import CustomerSearch from "./components/customer-search";
import EnterOtp from "./components/enter-otp";
import SelectAccounts from "./components/select-accounts";
import SelectContacts from "./components/select-contacts";
import SignIn from "components/sign-in";
import { gotoHomepage, handleFailure } from "./link-up-customer.helpers";
import LinkUpCustomerLogic, { getLogicFunctions, LinkUpCustomerMode } from "./link-up-customer.logic";

type LinkUpCustomerProps = {
    currentUserCheck: boolean;
    logic: LinkUpCustomerLogic;
}

const LinkUpCustomer = observer((props: LinkUpCustomerProps) => {
    const { currentUserCheck, logic } = props;

    const appContext = useContext(AppContext);
    const [updateTitle, updateBodyCssName] = useUpdatePage();
    const [checkTokenIimeout, setCheckTokenIimeout] = useState<NodeJS.Timeout>();
    const returnUrl = `${UrlUtils.getRootPath()}${BusinessPage.accountLinkUp.substring(1)}?action=${ApplicationAction.redirectOnly}`;
    const pageTitle = "Customer Search";

    const store = logic.store;

    const getUserDetailsAfterLinkUpAsync = async () => {
        try {
            const { status } = await getUserDetailsAsync();

            if (status !== userStatus.active) {
                console.error("Error getting user");
                handleFailure();
                return;
            }

            navigate(BusinessPage.accountCreated);
        } catch (error) {
            console.error("Error getting user details");
            logApiError(error);
            handleFailure();
        }
    }

    const onLinkupAsync = async () => {
        clearInterval(checkTokenIimeout);

        await getUserDetailsAfterLinkUpAsync();
    }

    const gotoDashboard = () => navigate(BusinessPage.dashboard);

    logic.setEvents(onLinkupAsync, gotoDashboard);

    const navigate = useNavigate();

    const cancellationToken = axios.CancelToken.source();

    const isMode = (m: number) => store.displayMode === m;

    const onLoad = () => {
        updateTitle(pageTitle);

        const countryCode = appContext.user?.countryCode ?? UrlUtils.getCountryCode();
        const bodyCss = `${Style.auth} link-up-customer country-${countryCode}`;
        updateBodyCssName(bodyCss);

        logic.checkToken();

        const checkTokenTimer = setInterval(() => {
            console.debug("Check token interval check");
            logic.checkToken();
        }, tokenCheckInterval);

        console.debug(`Check token interval check ${checkTokenTimer}`);

        setCheckTokenIimeout(checkTokenTimer);

        if (!currentUserCheck) {
            return;
        }

        logic.checkUser();
    }

    useEffectOnce(() => onLoad());

    const [search, selectAccounts, resendOtpAsync, sendOtpAsync, validateOtpAndLinkUpAsync] = getLogicFunctions(logic);

    return (
        <>
            {isMode(LinkUpCustomerMode.noAccess)
                ? <SignIn message="Your token has expired. Redirecting to sign in." returnUrl={returnUrl} />
                : null}

            <CustomerSearch
                display={isMode(LinkUpCustomerMode.search)}
                store={store}
                cancellationToken={cancellationToken}
                onSearch={search}
                onNotNow={gotoHomepage} />

            <AccountNotFound
                display={isMode(LinkUpCustomerMode.notFound)}
                onRetry={(e: any) => store.updateDisplayMode(LinkUpCustomerMode.search)} />

            <SelectAccounts
                display={isMode(LinkUpCustomerMode.select)}
                cancellationToken={cancellationToken}
                accounts={store.accounts}
                onSelect={selectAccounts}
                onTryOtherDetails={(e: any) => store.updateDisplayMode(LinkUpCustomerMode.search)} />

            <SelectContacts
                display={isMode(LinkUpCustomerMode.confirm)}
                cancellationToken={cancellationToken}
                store={store}
                onSend={sendOtpAsync}
                onGoBack={(e: any) => store.updateDisplayMode(LinkUpCustomerMode.select)} />

            <EnterOtp
                display={isMode(LinkUpCustomerMode.enterOtp)}
                cancellationToken={cancellationToken}
                errorMessage={store.otpErrorMessage}
                store={store}
                onOtpChange={() => store.updateOtpErrorMessage(undefined)}
                onResend={resendOtpAsync}
                onValidate={validateOtpAndLinkUpAsync}
                onCancel={(e: any) => {
                    store.updateOtpErrorMessage(undefined);
                    store.updateDisplayMode(LinkUpCustomerMode.select);
                }} />

            {isMode(LinkUpCustomerMode.getUser)
                ? <Notice showSpinner={true}>
                    <p>
                        Just finishing linking up your account...
                    </p>
                </Notice>
                : null}
        </>
    );
});

export default LinkUpCustomer;